import * as Either from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import isFunction from 'lodash-es/isFunction';
import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';
import { useEffect, useState, useCallback, } from 'react';
import { useQuery, } from '@tanstack/react-query';
import * as RemoteData from '../RemoteData';
import * as ErrorAsync from '../ErrorAsync';
import { useRemoteDataFailure } from './useRemoteData';
/**
 * A hook which wraps the `useQuery` hook from React query with RemoteData
 *
 * @see
 * https://tanstack.com/query/v4/docs/react/reference/useQuery
 *
 * @example
 *
 * ```tsx
 * import * as Either from 'fp-ts/Either';
 *
 * import { Http, RemoteData, useRemoteQuery } from '@catawiki/runtime-toolkit';
 *
 * const fetchUser = () => Promise.resolve(Either.right({ name: 'Jane Doe' }));
 *
 * const MyComponent = () => {
 *   const { rd } = useRemoteQuery({ queryKey: ['fetchUser'], queryFn: fetchUser });
 *
 *   return (
 *     <div>
 *       {RemoteData.match({
 *         idle: () => null,
 *         loading: () => <span>loading ...</span>,
 *         success: ({ name }) => <div>Name: {name}</div>,
 *         failure: error => (
 *           <span>Something went wrong: {JSON.stringify(error, null, 2)}</span>
 *         ),
 *       })(rd)}
 *     </div>
 *   );
 * };
 * ```
 *
 * @deprecated in favour of `useQuery`
 */
export const useRemoteQuery = ({ retry = 3, enabled, initialData: getInitialData, ...restOptions }) => {
    const [rd, setRd] = useState(() => {
        if (!isUndefined(enabled) && !enabled) {
            return RemoteData.idle;
        }
        const initialData = isFunction(getInitialData)
            ? getInitialData()
            : getInitialData;
        if (!initialData) {
            return RemoteData.initialLoading;
        }
        return pipe(initialData, Either.match(() => RemoteData.initialLoading, RemoteData.loading));
    });
    const query = useQuery({
        ...restOptions,
        initialData: getInitialData,
        retry,
        enabled,
    });
    const context = restOptions.queryKey?.find(isString) ?? 'missing_context';
    const getRemoteData = useCallback(() => {
        if (query.isFetching) {
            if (!query.data) {
                return RemoteData.initialLoading;
            }
            return pipe(query.data, Either.match(() => RemoteData.initialLoading, RemoteData.loading));
        }
        if (query.isError) {
            return RemoteData.failure(ErrorAsync.unknown(query.error));
        }
        if (query.isSuccess) {
            return pipe(query.data, Either.match(RemoteData.failure, RemoteData.success));
        }
        return RemoteData.idle;
    }, [
        query.data,
        query.error,
        query.isError,
        query.isFetching,
        query.isSuccess,
    ]);
    useEffect(() => {
        setRd(getRemoteData());
    }, [getRemoteData]);
    useRemoteDataFailure(rd, useCallback((errorData) => {
        ErrorAsync.notify(context, errorData);
    }, [context]));
    return { rd, setRd, ...query };
};
